import { globalStore } from '../Redux/initStore';
import { defaultMeasureRatio, eplassSlice } from '../Redux/EplassSlice';
import { selectedSidepanelSlice } from '../Redux/SelectedSidepanelSlice';
import { selectedStateSlice } from '../Redux/SelectedStateSlice';
import { PdfViewerViewModeFlag } from './Enums';
import { GlobalVariables } from './GlobalVariables';
import type {
    DocumentApiHistory,
    ITextDialogAttributes,
    IPresetLists,
    IPresetSelections,
    IUserStamp,
} from './Interfaces';
import { searchSlice } from '../Redux/SearchSlice';
import { ApryseSDK } from './ApryseSDK';

export class ReduxUpdater {
    private static _documentSaved = true;
    private static _allowWhileTextEditingCommands: string[] = [];
    private static _autosave = false;
    private static _compareColorAdded = 16711680;
    private static _compareColorRemoved = 65535;
    private static _compareMode: null | number = null;
    private static _documentHistory: DocumentApiHistory[] = [];
    private static _documentHistory2: DocumentApiHistory[] = [];
    private static _documentUrl = '';
    private static _documentUrl2 = '';
    private static _documentVersion = '';
    private static _documentVersion2 = '';
    private static _documentName = '';
    private static _documentName2 = '';
    private static _documentSubject = '';
    private static _documentSubject2 = '';
    private static _documentNoteID = '';
    private static _documentNoteID2 = '';
    private static _editmodeOnlyCommands: string[] = [];
    private static _virtualOnlyCommands: string[] = [];
    private static _lists: IPresetLists = {
        colors: [],
        fillColors: [],
        lineWidths: [],
        lineStyles: [],
        lineStylesCloudy: [],
        lineStarts: [],
        lineEnds: [],
        stamps: [],
        userStamps: [],
        userUnits: [],
        realUnits: [],
    };
    private static _arrowEnd = 'openArrow';
    private static _arrowStart = 'none';
    private static _color = 1;
    private static _fillColor = 0;
    private static _fontAttributes: ITextDialogAttributes;
    private static _lineWidth = 2.0;
    private static _lineStyle = 'solid';
    private static _lineCloudyStyle = 'cloudy small';
    private static _lineStart = 'none';
    private static _lineEnd = 'none';
    private static _measureRatio = defaultMeasureRatio;
    private static _shortcuts = {};
    private static _stamp = 'Approved';
    private static _thumbnailSize = 3;
    private static _viewMode = PdfViewerViewModeFlag.Loading;

    static useWorkflowColor = false;
    static isOnSearch = false;

    private static _stateName = 'hand';

    static previousStateName = 'zoomByBox';

    static annotationSidepanelRefresh() {
        globalStore.dispatch(eplassSlice.actions.forceAnnotSidepanelRefresh());
    }

    static setSavingDocument(isSaving: boolean) {
        globalStore.dispatch(eplassSlice.actions.changeDocumentSavingChanges(isSaving));
    }

    static ribbonRefresh() {
        globalStore.dispatch(eplassSlice.actions.forceRibbonRefresh());
    }

    static toggleUserAutosave() {
        this._autosave = !this._autosave;
        globalStore.dispatch(eplassSlice.actions.changeUserAutosave(this._autosave));
    }

    static undoRedoRefresh() {
        globalStore.dispatch(eplassSlice.actions.forceUndoRedoRefresh());
    }

    static userSettingsDialogRefresh() {
        globalStore.dispatch(eplassSlice.actions.forceUserSettingsDialogRefresh());
    }

    static set selectedSidepanel(sidepanelName: string | null) {
        globalStore.dispatch(selectedSidepanelSlice.actions.changeSidepanelName(sidepanelName));
    }

    static get documentSaved(): boolean {
        return this._documentSaved;
    }

    static set documentSaved(newValue: boolean) {
        this._documentSaved = newValue;
        globalStore.dispatch(eplassSlice.actions.changeDocumentSaved(newValue));
    }

    static get thumbnailSize(): number {
        return this._thumbnailSize;
    }

    static set thumbnailSize(size: number) {
        this._thumbnailSize = size;
        globalStore.dispatch(selectedSidepanelSlice.actions.setThumbnailSize(size));
    }

    static get stateName(): string {
        return this._stateName;
    }

    static set stateName(stateName: string) {
        this._stateName = stateName;
        globalStore.dispatch(selectedStateSlice.actions.changeStateName(stateName));
        if (stateName === ApryseSDK.ToolsENUM.CREATE_ARROW) {
            ReduxUpdater.selectedArrowStart = 'none';
            ReduxUpdater.selectedArrowEnd = 'openArrow';
        }
    }

    static get viewMode(): PdfViewerViewModeFlag {
        return this._viewMode;
    }

    static set viewMode(mode: PdfViewerViewModeFlag) {
        this._viewMode = mode;
        globalStore.dispatch(eplassSlice.actions.changeViewMode(mode));
    }

    static get compareColorAdded(): number {
        return this._compareColorAdded;
    }

    static set compareColorAdded(compareColorAdded: number) {
        this._compareColorAdded = compareColorAdded;
    }

    static get compareColorRemoved(): number {
        return this._compareColorRemoved;
    }

    static set compareColorRemoved(compareColorRemoved: number) {
        this._compareColorRemoved = compareColorRemoved;
    }

    static get compareMode() {
        return this._compareMode;
    }

    static set compareMode(compareMode: null | number) {
        this._compareMode = compareMode;
    }

    static get documentHistory(): DocumentApiHistory[] {
        return this._documentHistory;
    }

    static set documentHistory(documentHistory: DocumentApiHistory[]) {
        this._documentHistory = documentHistory;
    }

    static get documentHistory2(): DocumentApiHistory[] {
        return this._documentHistory2;
    }

    static set documentHistory2(documentHistory: DocumentApiHistory[]) {
        this._documentHistory2 = documentHistory;
    }

    static get documentUrl(): string {
        return this._documentUrl;
    }

    static set documentUrl(documentUrl: string) {
        this._documentUrl = documentUrl.replace('/', '');
        globalStore.dispatch(eplassSlice.actions.changeDocumentUrl(documentUrl));
    }

    static get documentUrl2(): string {
        return this._documentUrl2;
    }

    static get documentVersion(): string {
        return this._documentVersion;
    }

    static set documentVersion(version: string) {
        this._documentVersion = version;
        this.updateDocumentTitle();
        globalStore.dispatch(eplassSlice.actions.changeDocumentVersion(version));
    }

    static get documentVersion2(): string {
        return this._documentVersion2;
    }

    static set documentVersion2(version: string) {
        this._documentVersion2 = version;
    }

    static get documentName(): string {
        return this._documentName;
    }

    static set documentName(documentName: string) {
        this._documentName = documentName;
        this.updateDocumentTitle();
        globalStore.dispatch(eplassSlice.actions.changeDocumentName(documentName));
    }

    static get documentName2(): string {
        return this._documentName2;
    }

    static set documentName2(documentName: string) {
        this._documentName2 = documentName;
    }

    static get documentSubject(): string {
        return this._documentSubject;
    }

    static set documentSubject(documentSubject: string) {
        this._documentSubject = documentSubject;
        globalStore.dispatch(eplassSlice.actions.changeDocumentSubject(documentSubject));
    }

    static get documentSubject2(): string {
        return this._documentSubject2;
    }

    static set documentSubject2(documentSubject: string) {
        this._documentSubject2 = documentSubject;
    }

    static get documentNoteID(): string {
        return this._documentNoteID;
    }

    static set documentNoteID(documentNoteId: string) {
        this._documentNoteID = documentNoteId;
    }

    static get documentNoteID2(): string {
        return this._documentNoteID2;
    }

    static set documentNoteID2(documentNoteId: string) {
        this._documentNoteID2 = documentNoteId;
    }

    static get lists(): IPresetLists {
        return this._lists;
    }

    static set lists(lists: IPresetLists) {
        this._lists = { ...this._lists, ...lists };
        globalStore.dispatch(eplassSlice.actions.changeLists(this._lists));
        GlobalVariables.loadUserCategories();
        ApryseSDK.updateUserStamps();
    }

    static get selections(): IPresetSelections {
        return {
            color: this._color,
            fillColor: this._fillColor,
            textAttributes: this._fontAttributes,
            lineWidth: this._lineWidth,
            lineStyle: this._lineStyle,
            lineCloudyStyle: this._lineCloudyStyle,
            lineStart: this._lineStart,
            lineEnd: this._lineEnd,
            stampName: this._stamp,
            measureRatio: this._measureRatio,
        } as IPresetSelections;
    }

    static set selections(selections: IPresetSelections) {
        this._color = selections.color;
        this._fillColor = selections.fillColor;
        this._fontAttributes = selections.textAttributes;
        this._lineEnd = selections.lineEnd;
        this._lineStart = selections.lineStart;
        this._lineStyle = selections.lineStyle;
        this._lineCloudyStyle = selections.lineCloudyStyle;
        this._lineWidth = selections.lineWidth;
        this._measureRatio = selections.measureRatio;
        this._stamp = selections.stampName;
        globalStore.dispatch(eplassSlice.actions.changeSelections(selections));
    }

    static get shortcuts() {
        return this._shortcuts;
    }

    static set shortcuts(shortcuts) {
        this._shortcuts = shortcuts;
    }

    static get allowWhileTextEditingCommands() {
        return this._allowWhileTextEditingCommands;
    }

    static set allowWhileTextEditingCommands(allowWhileTextEditingCommands) {
        this._allowWhileTextEditingCommands = allowWhileTextEditingCommands;
    }

    static get editmodeOnlyCommands() {
        return this._editmodeOnlyCommands;
    }

    static set editmodeOnlyCommands(editmodeOnlyCommands) {
        this._editmodeOnlyCommands = editmodeOnlyCommands;
    }

    static get virtualOnlyCommands() {
        return this._virtualOnlyCommands;
    }

    static set virtualOnlyCommands(virtualOnlyCommands) {
        this._virtualOnlyCommands = virtualOnlyCommands;
    }

    static get selectedColor(): number {
        return this._color;
    }

    static set selectedColor(code: number) {
        this._color = code;
        globalStore.dispatch(eplassSlice.actions.changeSelectedColor(code));
    }

    static get selectedFillColor(): number {
        return this._fillColor;
    }

    static set selectedFillColor(code: number) {
        this._fillColor = code;
        globalStore.dispatch(eplassSlice.actions.changeSelectedFillColor(code));
    }

    static get selectedFontAttributes(): ITextDialogAttributes {
        return this._fontAttributes;
    }

    static set selectedFontAttributes(attributes: ITextDialogAttributes) {
        if (!attributes) {
            return;
        }
        this._fontAttributes = attributes;
        globalStore.dispatch(
            eplassSlice.actions.changeSelectedFontAttributes(this._fontAttributes),
        );
    }

    static get selectedLineWidth(): number {
        return this._lineWidth;
    }

    static set selectedLineWidth(width: number) {
        this._lineWidth = width;
        globalStore.dispatch(eplassSlice.actions.changeSelectedLineWidth(width));
    }

    static get selectedLineStyle(): string {
        return this._lineStyle;
    }

    static set selectedLineStyle(style: string) {
        this._lineStyle = style;
        globalStore.dispatch(eplassSlice.actions.changeSelectedLineStyle(style));
    }

    static get selectedLineCloudyStyle(): string {
        return this._lineCloudyStyle;
    }

    static set selectedLineCloudyStyle(style: string) {
        this._lineCloudyStyle = style;
        globalStore.dispatch(eplassSlice.actions.changeSelectedLineCloudyStyle(style));
    }

    static get selectedArrowStart(): string {
        return this._arrowStart;
    }

    static set selectedArrowStart(start: string) {
        this._arrowStart = start;
        globalStore.dispatch(eplassSlice.actions.changeSelectedArrowStart(start));
    }

    static get selectedArrowEnd(): string {
        return this._arrowEnd;
    }

    static set selectedArrowEnd(end: string) {
        this._arrowEnd = end;
        globalStore.dispatch(eplassSlice.actions.changeSelectedArrowEnd(end));
    }

    static get selectedLineStart(): string {
        return this._lineStart;
    }

    static set selectedLineStart(start: string) {
        this._lineStart = start;
        globalStore.dispatch(eplassSlice.actions.changeSelectedLineStart(start));
    }

    static get selectedLineEnd(): string {
        return this._lineEnd;
    }

    static set selectedLineEnd(end: string) {
        this._lineEnd = end;
        globalStore.dispatch(eplassSlice.actions.changeSelectedLineEnd(end));
    }

    static get selectedStamp(): string {
        return this._stamp;
    }

    static set selectedStamp(stamp: string) {
        this._stamp = stamp;
        ApryseSDK.setStamp(stamp);
        globalStore.dispatch(eplassSlice.actions.changeSelectedStamp(stamp));
    }

    static update(data: { documentUrl?: string; viewMode?: PdfViewerViewModeFlag }) {
        globalStore.dispatch(eplassSlice.actions.update(data));
    }

    static updateDocumentTitle() {
        if (this._documentName) {
            const docIndex = `${
                this._documentVersion?.trim() ? ' - ' + this._documentVersion : ''
            }`;
            document.title = `${this._documentName}${docIndex}`;
            return;
        }
        if (this._documentSubject) {
            document.title = `${this._documentSubject}`;
        }
    }

    static addUserStamp(addUserStamp: IUserStamp) {
        const newUserStamps = [...(ReduxUpdater.lists.userStamps ?? []), addUserStamp];
        ReduxUpdater.lists = {
            ...ReduxUpdater.lists,
            userStamps: newUserStamps,
        };
        ApryseSDK.updateUserStamps();
        this.userSettingsDialogRefresh();
    }

    static removeUserStamps(removeUserStampsIds: string[]) {
        const newUserStamps = ReduxUpdater.lists.userStamps ?? [];
        ReduxUpdater.lists = {
            ...ReduxUpdater.lists,
            userStamps: newUserStamps.filter(stamp => !removeUserStampsIds.includes(stamp.id)),
        };
        ApryseSDK.updateUserStamps();
        this.userSettingsDialogRefresh();
    }

    static replaceUserStamps(newUserStamps: IUserStamp[]) {
        ReduxUpdater.lists = {
            ...ReduxUpdater.lists,
            userStamps: newUserStamps,
        };
        ApryseSDK.updateUserStamps();
        this.userSettingsDialogRefresh();
    }

    static setDocumentUrls(documentUrl1: string, documentUrl2: string) {
        this._documentUrl = documentUrl1.replace('/', '');
        this._documentUrl2 = documentUrl2.replace('/', '');
        globalStore.dispatch(
            eplassSlice.actions.changeDocumentsUrl([documentUrl1, this.documentUrl2]),
        );
    }

    static toggleIsOnSearch(state: boolean) {
        this.isOnSearch = state;
        globalStore.dispatch(searchSlice.actions.changeIsOnSearch(this.isOnSearch));
    }
}
